import { CalendarDays, ChevronDown, Phone } from 'lucide-react';
import React, { type ReactNode } from 'react';

import { getShiftDurationH } from '@apps/web/components/dashboard/shifts_page_heading';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { Markdown } from '@apps/web/components/ui/markdown';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { formatPhoneNumber } from '@apps/web/lib/contact_utils';
import {
    formatDayOfWeek,
    formatFullDate,
    formatTime,
} from '@apps/web/lib/date_utils';
import {
    formatExternalStatus,
    formatOfficeName,
} from '@apps/web/lib/format_utils';
import { cn } from '@apps/web/lib/utils';
import { type Shift } from '@libraries/typescript/api_client';

export function ShiftDetailsField({
    label,
    children,
    className,
    inline = true,
}: {
    readonly label: string;
    readonly children: ReactNode;
    readonly className?: string;
    readonly inline?: boolean;
}) {
    return (
        <div
            className={cn(
                inline &&
                    'grid min-w-0 grid-cols-[minmax(0,6.5rem)_minmax(0,1fr)] items-baseline gap-x-3 text-sm leading-5',
                className,
            )}
        >
            <dt
                className={cn(
                    'text-muted-foreground',
                    !inline && 'text-sm font-medium',
                )}
            >
                {label}
                {inline && ':'}
            </dt>
            <dd className={inline ? 'min-w-0 break-words' : 'text-sm'}>
                {children}
            </dd>
        </div>
    );
}

type ShiftDetailsSidebarProps = {
    readonly shift: Shift;
    readonly timezone: string;
    readonly status: ReactNode;
    readonly clientReferenceTermTitle: string;
    readonly clientPhoneNumber: string | null;
    readonly onViewClient?: React.MouseEventHandler<HTMLButtonElement>;
    readonly caregiverControl: ReactNode;
    readonly caregiverPhones: ReadonlyArray<{
        readonly number: string;
        readonly isPrimary: boolean;
    }>;
    readonly ehrStatusLabel: string;
    readonly userIsAdmin: boolean;
    readonly adminControls: ReactNode;
    readonly redesigned: boolean;
    readonly clientAction?: ReactNode;
    readonly showDate?: boolean;
};

export function ShiftDetailsSidebar(props: ShiftDetailsSidebarProps) {
    return props.redesigned ? (
        <RedesignedShiftDetailsSidebar {...props} />
    ) : (
        <LegacyShiftDetailsSidebar {...props} />
    );
}

function RedesignedShiftDetailsSidebar({
    shift,
    timezone,
    status,
    clientReferenceTermTitle,
    clientPhoneNumber,
    onViewClient,
    caregiverControl,
    caregiverPhones,
    ehrStatusLabel,
    userIsAdmin,
    adminControls,
}: ShiftDetailsSidebarProps) {
    const { formatName } = useFormatPersonName();
    const cityAndState = [shift.patient_city, shift.patient_state]
        .filter(Boolean)
        .join(', ');
    const durationHours = getShiftDurationH(shift);

    return (
        <aside
            aria-label="Shift information"
            data-testid="shift-details-sidebar"
            className="min-w-0 shrink-0 space-y-5 md:min-h-0 md:overflow-y-auto md:pr-4"
        >
            <section
                aria-label="Schedule"
                className="bg-background ring-border rounded-lg p-4 ring-1 ring-inset"
            >
                <div className="text-muted-foreground flex items-center gap-2 text-xs">
                    <CalendarDays className="size-4 shrink-0" />
                    <span>
                        {formatDayOfWeek(shift.shift_start_time, timezone)},{' '}
                        {formatFullDate(shift.shift_start_time, timezone)}
                    </span>
                </div>
                <p className="mt-2 text-xl leading-tight font-semibold tracking-tight tabular-nums">
                    {formatTime(shift.shift_start_time, timezone)} –{' '}
                    {formatTime(shift.shift_end_time, timezone)}
                </p>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-baseline gap-1.5 text-xs">
                        <span className="text-muted-foreground">Status:</span>
                        {status}
                    </div>
                    {Number.isFinite(durationHours) && durationHours > 0 && (
                        <span className="text-muted-foreground text-xs tabular-nums">
                            {durationHours.toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                            })}{' '}
                            {durationHours === 1 ? 'hour' : 'hours'}
                        </span>
                    )}
                </div>
            </section>
            <dl className="space-y-4 px-4">
                <ShiftDetailsField label={clientReferenceTermTitle}>
                    <div className="flex min-w-0">
                        {onViewClient ? (
                            <Button
                                variant="link"
                                className="h-auto min-w-0 shrink p-0 text-left font-semibold break-words whitespace-normal"
                                onClick={onViewClient}
                            >
                                {formatName(shift.patient_name) ||
                                    'View client'}
                            </Button>
                        ) : (
                            <p className="min-w-0 font-semibold break-words">
                                {formatName(shift.patient_name) ||
                                    'Not specified'}
                            </p>
                        )}
                    </div>
                    {clientPhoneNumber && (
                        <p className="text-muted-foreground mt-1 flex items-center gap-1.5 text-xs">
                            <Phone className="size-3.5 shrink-0" />
                            <span>{formatPhoneNumber(clientPhoneNumber)}</span>
                        </p>
                    )}
                </ShiftDetailsField>
                <ShiftDetailsField label="Caregiver">
                    <div className="font-medium">{caregiverControl}</div>
                    {shift.role_required && (
                        <Badge variant="secondary" className="mt-1 text-xs">
                            {shift.role_required}
                        </Badge>
                    )}
                    {caregiverPhones.length > 0 && (
                        <ul className="text-muted-foreground mt-2 space-y-1 text-xs">
                            {caregiverPhones.map((phone, index) => (
                                <li
                                    key={`${phone.number}-${index}`}
                                    className="flex items-center gap-1.5"
                                >
                                    <Phone className="size-3.5 shrink-0" />
                                    <span
                                        className={cn(
                                            phone.isPrimary && 'font-medium',
                                        )}
                                    >
                                        {formatPhoneNumber(phone.number)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </ShiftDetailsField>
            </dl>
            <dl className="space-y-3 border-t px-4 pt-4">
                <ShiftDetailsField label="Location">
                    <p>
                        {shift.patient_address ||
                            (!cityAndState && 'No address provided')}
                    </p>
                    {cityAndState && (
                        <p className="text-muted-foreground">{cityAndState}</p>
                    )}
                </ShiftDetailsField>
                <ShiftDetailsField label="Office">
                    {formatOfficeName(shift.patient_office_name)}
                </ShiftDetailsField>
            </dl>
            {shift.patient_care_plan && (
                <dl className="border-t px-4 pt-4">
                    <ShiftDetailsField label="Care plan">
                        <Markdown className="text-sm leading-6">
                            {shift.patient_care_plan}
                        </Markdown>
                    </ShiftDetailsField>
                </dl>
            )}
            <Collapsible key={shift.id} className="border-t pt-2">
                <CollapsibleTrigger className="group hover:bg-muted focus-visible:ring-ring flex w-full items-center justify-between rounded-md px-4 py-2 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none">
                    More details
                    <ChevronDown className="text-muted-foreground size-4 transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 px-4 pt-3 pb-2">
                    <dl className="space-y-3">
                        <ShiftDetailsField label={ehrStatusLabel}>
                            {shift.external_status
                                ? formatExternalStatus(shift.external_status)
                                : '—'}
                        </ShiftDetailsField>
                        <ShiftDetailsField label="Role">
                            {shift.role_required || '—'}
                        </ShiftDetailsField>
                        <ShiftDetailsField label="Last updated">
                            {formatFullDate(shift.updated_at, timezone)}
                            {' · '}
                            {formatTime(shift.updated_at, timezone)}
                        </ShiftDetailsField>
                        <ShiftDetailsField label="Last synced">
                            {shift.external_snapshot_synced_at ? (
                                <React.Fragment>
                                    {formatFullDate(
                                        shift.external_snapshot_synced_at,
                                        timezone,
                                    )}
                                    {' · '}
                                    {formatTime(
                                        shift.external_snapshot_synced_at,
                                        timezone,
                                    )}
                                </React.Fragment>
                            ) : (
                                '—'
                            )}
                        </ShiftDetailsField>
                        {userIsAdmin && (
                            <React.Fragment>
                                <ShiftDetailsField label="Frequency">
                                    {shift.frequency.toUpperCase()}
                                </ShiftDetailsField>
                                <ShiftDetailsField label="External ID">
                                    <span className="font-mono text-xs">
                                        {shift.external_id || '—'}
                                    </span>
                                </ShiftDetailsField>
                            </React.Fragment>
                        )}
                    </dl>
                    {userIsAdmin && adminControls && (
                        <dl className="space-y-3 border-t pt-4">
                            {adminControls}
                        </dl>
                    )}
                </CollapsibleContent>
            </Collapsible>
        </aside>
    );
}

function LegacyShiftDetailsSidebar({
    shift,
    timezone,
    status,
    clientReferenceTermTitle,
    clientPhoneNumber,
    clientAction,
    caregiverControl,
    caregiverPhones,
    ehrStatusLabel,
    userIsAdmin,
    adminControls,
    showDate,
}: ShiftDetailsSidebarProps) {
    const { formatName } = useFormatPersonName();
    return (
        <div className="space-y-2 overflow-y-auto md:col-span-2 md:pr-4">
            <h3 className="text-lg font-semibold">Shift Information</h3>
            <dl className="grid grid-cols-1 gap-2">
                <ShiftDetailsField label="Time" inline={false}>
                    <p>
                        {formatTime(shift.shift_start_time, timezone)} -{' '}
                        {formatTime(shift.shift_end_time, timezone)}
                    </p>
                    {showDate && (
                        <p className="text-muted-foreground text-xs">
                            {formatFullDate(shift.shift_start_time, timezone)}
                        </p>
                    )}
                </ShiftDetailsField>
                <ShiftDetailsField label="Status" inline={false}>
                    {status}
                </ShiftDetailsField>
                <div className="flex flex-col gap-2">
                    <dt className="flex items-center gap-2">
                        <span className="text-muted-foreground text-sm font-medium">
                            {ehrStatusLabel}
                        </span>
                        {shift.external_snapshot_synced_at && (
                            <span className="text-muted-foreground text-xs">
                                (Last synced at{' '}
                                {formatTime(
                                    shift.external_snapshot_synced_at,
                                    timezone,
                                )}
                                )
                            </span>
                        )}
                    </dt>
                    <dd
                        className={
                            shift.external_status
                                ? 'border-border text-muted-foreground w-fit rounded border px-1 text-[9px] uppercase'
                                : 'text-muted-foreground text-sm'
                        }
                    >
                        {shift.external_status
                            ? formatExternalStatus(shift.external_status)
                            : '—'}
                    </dd>
                </div>
                <ShiftDetailsField
                    label={clientReferenceTermTitle}
                    inline={false}
                >
                    <div className="flex items-center gap-2">
                        <p>{formatName(shift.patient_name)}</p>
                        {clientAction}
                    </div>
                </ShiftDetailsField>
                <ShiftDetailsField
                    label={`${clientReferenceTermTitle} Phone`}
                    inline={false}
                >
                    {clientPhoneNumber || '—'}
                </ShiftDetailsField>
                <ShiftDetailsField label="Caregiver" inline={false}>
                    {caregiverControl}
                </ShiftDetailsField>
                <ShiftDetailsField label="Role" inline={false}>
                    {shift.role_required || '—'}
                </ShiftDetailsField>
                <ShiftDetailsField label="Caregiver Phone" inline={false}>
                    {caregiverPhones.length === 0 ? (
                        '—'
                    ) : caregiverPhones.length === 1 ? (
                        caregiverPhones[0]!.number
                    ) : (
                        <ul className="list-disc pl-4">
                            {caregiverPhones.map((phone, index) => (
                                <li
                                    key={index}
                                    className={
                                        phone.isPrimary ? 'font-semibold' : ''
                                    }
                                >
                                    {phone.number}
                                </li>
                            ))}
                        </ul>
                    )}
                </ShiftDetailsField>
                <ShiftDetailsField label="Address" inline={false}>
                    {shift.patient_address || '—'}
                </ShiftDetailsField>
                <ShiftDetailsField label="City" inline={false}>
                    {shift.patient_city || '—'}
                </ShiftDetailsField>
                <ShiftDetailsField label="State" inline={false}>
                    {shift.patient_state || '—'}
                </ShiftDetailsField>
                <ShiftDetailsField label="Office" inline={false}>
                    {formatOfficeName(shift.patient_office_name)}
                </ShiftDetailsField>
                <ShiftDetailsField label="Last Updated" inline={false}>
                    {formatTime(shift.updated_at, timezone)}
                </ShiftDetailsField>
                <ShiftDetailsField label="Last Synced" inline={false}>
                    {shift.external_snapshot_synced_at
                        ? `${formatFullDate(
                              shift.external_snapshot_synced_at,
                              timezone,
                          )} ${formatTime(shift.external_snapshot_synced_at, timezone)}`
                        : '—'}
                </ShiftDetailsField>
                {userIsAdmin && (
                    <React.Fragment>
                        <ShiftDetailsField label="External ID" inline={false}>
                            <span className="font-mono">
                                {shift.external_id || '—'}
                            </span>
                        </ShiftDetailsField>
                        <ShiftDetailsField label="Frequency" inline={false}>
                            {shift.frequency.toUpperCase()}
                        </ShiftDetailsField>
                        {adminControls}
                    </React.Fragment>
                )}
            </dl>
            {shift.patient_care_plan && (
                <React.Fragment>
                    <h3 className="mt-6 border-t pt-4 text-lg font-semibold">
                        Care Plan
                    </h3>
                    <div className="mt-4 rounded-lg border p-4">
                        <Markdown className="text-sm">
                            {shift.patient_care_plan}
                        </Markdown>
                    </div>
                </React.Fragment>
            )}
        </div>
    );
}
